import { J as JS_PREFIX, r as IS_HIDDEN_CLASS, t as translate } from './built-constants-439d5572.js';
import './built-price-format-dc3d77ee.js';
import { g as getStoreOpenClose } from './built-get-url-variant-a647ef33.js';

var _window$DEC_GLOBALS;
const JS_PRODUCT_FULFILLMENT_PREFIX = JS_PREFIX + "ProductFulfillment-";
const STORE_PICKUP_PREFIX = JS_PREFIX + "StorePickup-";
/**
 * Page (in buybox fulfillment section) selectors
 */

const STORE_PICKUP_OPTIONS = JS_PRODUCT_FULFILLMENT_PREFIX + "pickupOptions";
const STORE_PICKUP_DETAILS = JS_PRODUCT_FULFILLMENT_PREFIX + "pickupDetails";
const PICKUP_TIME_FRAME = JS_PRODUCT_FULFILLMENT_PREFIX + "pickupTimeFrame";
const STORE_PICKUP_CITY = JS_PRODUCT_FULFILLMENT_PREFIX + "pickupCity";
const STORE_PICKUP_ADDRESS = JS_PRODUCT_FULFILLMENT_PREFIX + "pickupAddress";
const SELECT_FOR_PICKUP_OPTIONS_MESSAGE = JS_PRODUCT_FULFILLMENT_PREFIX + "selectForOptionsMessage";
/**
 * Drawer store-list and location selectors
 */

const STORE_PICKUP_LIST_SELECTOR = STORE_PICKUP_PREFIX + "list";
const STORE_PICKUP_USER_CITY_SELECTOR = STORE_PICKUP_PREFIX + "city";
const STORE_PICKUP_USER_STATE_SELECTOR = STORE_PICKUP_PREFIX + "stateCode";
const STORE_PICKUP_THUMBNAIL_IMAGE = STORE_PICKUP_PREFIX + "thumbnailImage";
const STORE_PICKUP_VARIANT_SIZE = STORE_PICKUP_PREFIX + "variantSize";
const STORE_PICKUP_VARIANT_COLOR = STORE_PICKUP_PREFIX + "variantColor";
const STORE_PICKUP_LOCATION_UPDATE_MESSAGE = STORE_PICKUP_PREFIX + "locationUpdateMessage";
const STORE_PICKUP_DEFAULT_LOCATION_UPDATE_MESSAGE = STORE_PICKUP_PREFIX + "defaultLocationUpdateMessage";
const STORE_PICKUP_LOCATION_UPDATE_MESSAGE_CONTENT = STORE_PICKUP_PREFIX + "locationUpdateMessageContent";
/**
 * Store Pickup Radius
 * Maximum distance (in miles) between user's location and store locations for in-store pickup
 * For testing purposes, the OUT_OF_AREA_THRESHOLD environment variable can be adjusted
 */

const OUT_OF_AREA_THRESHOLD =  ((_window$DEC_GLOBALS = window.DEC_GLOBALS) == null ? void 0 : _window$DEC_GLOBALS.storePickupRadius) || 15;
/**
 * @TODO - For future implementation, TODAY will be possible for pickup from
 * in-store inventory. Additionally, a range of days will be needed (likely
 * as an enum, to be conditioned by days from a warehouse to store, starting
 * with the current day)
 */

/**
 * Constants for updating classLists
 */

const REMOVE = 'remove';
const ADD = 'add';

/**
 * Add distance-related fields to store objects
 *
 * @param {Object} data The Decathlon stores and corresponding distances
 * @param {Array} data.stores A collection of store locations
 * @param {Array} data.distances A collection of distance calculations
 * @returns {Array} A collection of stores
 */
const formatStores = ({
  stores,
  distances
}) => stores // Add required additional fields
.map((store, i) => Object.assign({}, store, {
  distance: distances[i],
  distanceFloat: parseFloat(distances[i].replace(/,/g, ''))
}));
/**
 * Get stores within a distance threshold, based on
 * @param {Object} data The Decathlon stores and corresponding distances
 * @param {Array} data.stores A collection of store locations
 * @param {number} data.threshold A value to gauge whether a store is within
 * allowed radius of a customer to be displayed as a pickup option
 * @returns {Array} A collection of stores
 */


const getStoresWithinDistanceThreshold = ({
  stores,
  threshold
}) => stores.filter( // Determine if the store is within an acceptable distance
store => store.distanceFloat <= threshold);
/**
 * Get and sort stores within a distance threshold, from nearest to farthest
 * @param {Object} data The Decathlon stores and corresponding distances
 * @param {Array} data.stores A collection of store locations
 * @param {Array} data.distances A collection of distance calculations
 * @param {string | number} data.threshold A value to gauge whether a store is within
 * allowed radius of a customer to be displayed as a pickup option
 * @returns {Array} A collection of stores, sorted by distance
 */


const getDistanceSortedStores = ({
  stores,
  distances,
  threshold
}) => {
  const storesWithinDistanceThreshold = getStoresWithinDistanceThreshold({
    stores: formatStores({
      stores,
      distances
    }),
    threshold: typeof threshold === 'string' ? parseFloat(threshold) : threshold
  });
  return storesWithinDistanceThreshold.sort( // Rearrange the stores from nearest to farthest
  (a, b) => a.distanceFloat - b.distanceFloat);
};
/**
 * Show an element and return it for additional DOM method chaining
 * @param {HTMLElement} element
 * @returns {HTMLElement} The same element
 */

const showElement = element => {
  element.classList.remove(IS_HIDDEN_CLASS);
  return element;
};
/**
 * Hide an element and return it for additional DOM method chaining
 * @param {HTMLElement} element
 * @returns {HTMLElement} The same element
 */

const hideElement = element => {
  element.classList.add(IS_HIDDEN_CLASS);
  return element;
};
/**
 * Calculates a distance estimation between two points on a sphere as the crow flies (aka, a straight line).
 * This estimation is acceptable for the requirements of the UI in which it's displayed.
 * @see https://andrew.hedges.name/experiments/haversine/ for more details on the formula used
 *
 * @param {Object} data The Decathlon store and user location latitude/longitude
 * @param {Number} data.lat1 The first latitude
 * @param {Number} data.lon1 The first longitude
 * @param {Number} data.lat2 The second latitude
 * @param {Number} data.lon2 The second longitude
 * @returns {String} The distance between two points
 */

const estimateDistanceFromLatLngInMiles = ({
  lat1,
  lon1,
  lat2,
  lon2
}) => {
  const degreeToRadius = deg => deg * (Math.PI / 180); // Radius of the earth in miles


  const R = 3961;
  const dLat = degreeToRadius(lat2 - lat1);
  const dLon = degreeToRadius(lon2 - lon1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(degreeToRadius(lat1)) * Math.cos(degreeToRadius(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return d.toFixed(1);
};
/**
 * @enum { number }
 */

const PickupOption = {
  PICKUP_TOMORROW: 1,
  SELECT_FOR_OPTIONS: 2,
  ONE_HOUR_PICKUP: 3,
  PICKUP_TODAY: 4
};
/**
 * Based on the store's hours, returns the soonest available pickup option
 * Assumes that the store has the product in stock
 * @param {Store} store
 * @returns {PickupOption}
 */

const getSoonestPickupOption = store => {
  const currentDayOfWeek = new Date().getDay();
  /** Current hour of day, in PST (0-24) */

  const currentHourPST = getCurrentHourPST();
  const {
    open,
    close
  } = getStoreOpenClose({
    day: currentDayOfWeek,
    storeId: store.id
  }); // Requirement: range starts one hour before opening and 1 hour before closing

  if (currentHourPST < open - 1) {
    // Up to 1 hour before store opening
    return PickupOption.PICKUP_TODAY;
  }

  if (currentHourPST > close - 1) {
    // Within 1 hour of store closing
    return PickupOption.PICKUP_TOMORROW;
  } // Within 1 hour of store opening and 1 hour of store closing


  return PickupOption.ONE_HOUR_PICKUP;
};
/**
 * Finds the current time in the PST time zone
 * For example, if it is 9am it will return 9
 * If it is 1:30pm, it will return 13.5
 */

const getCurrentHourPST = () => {
  const formatted = timeFormatter.formatToParts(new Date());
  const hour = Number(formatted.find(segment => segment.type === 'hour').value);
  const minute = Number(formatted.find(segment => segment.type === 'minute').value);
  return hour + minute / 60;
};

const timeFormatter = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: 'numeric',
  timeZone: 'America/Los_Angeles',
  hour12: false
});
/**
 * @param {PickupOption} pickupOption
 */

const getPickupOptionText = pickupOption => {
  if (pickupOption === PickupOption.SELECT_FOR_OPTIONS) {
    return translate("Translation missing: es.products.fulfillment_options.pickup");
  }

  if (pickupOption === PickupOption.PICKUP_TOMORROW) {
    return translate("Translation missing: es.products.fulfillment_options.pickup_tomorrow");
  }

  if (pickupOption === PickupOption.PICKUP_TODAY) {
    return translate("Translation missing: es.products.fulfillment_options.pickup_today");
  }

  return translate("Translation missing: es.products.fulfillment_options.one_hour_pickup");
};

export { ADD as A, OUT_OF_AREA_THRESHOLD as O, PickupOption as P, REMOVE as R, STORE_PICKUP_LIST_SELECTOR as S, getDistanceSortedStores as a, STORE_PICKUP_USER_CITY_SELECTOR as b, STORE_PICKUP_USER_STATE_SELECTOR as c, STORE_PICKUP_ADDRESS as d, estimateDistanceFromLatLngInMiles as e, STORE_PICKUP_CITY as f, getSoonestPickupOption as g, PICKUP_TIME_FRAME as h, STORE_PICKUP_OPTIONS as i, STORE_PICKUP_DETAILS as j, SELECT_FOR_PICKUP_OPTIONS_MESSAGE as k, STORE_PICKUP_THUMBNAIL_IMAGE as l, STORE_PICKUP_VARIANT_COLOR as m, STORE_PICKUP_VARIANT_SIZE as n, STORE_PICKUP_LOCATION_UPDATE_MESSAGE as o, STORE_PICKUP_LOCATION_UPDATE_MESSAGE_CONTENT as p, STORE_PICKUP_DEFAULT_LOCATION_UPDATE_MESSAGE as q, getPickupOptionText as r, showElement as s, hideElement as t };
